/**
 * Instruments page - read-only browser for the funding instrument library.
 */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, BookOpen, Layers } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import instrumentService from '../services/instrumentService';
import {
  InstrumentLibraryItem,
  InstrumentListFilters,
  Complexity,
} from '../types/project';

const STAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'pre_seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'series_c', label: 'Series C' },
  { value: 'growth', label: 'Growth' },
  { value: 'late_stage', label: 'Late Stage' },
];

const COMPLEXITY_OPTIONS: Array<{ value: Complexity; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'very_high', label: 'Very High' },
];

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatMoney = (value?: number): string => {
  if (value === undefined || value === null) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

const complexityBadge = (c?: Complexity): string => {
  switch (c) {
    case 'low':
      return 'bg-green-100 text-green-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'high':
      return 'bg-orange-100 text-orange-800';
    case 'very_high':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const InstrumentsPage: React.FC = () => {
  const [instruments, setInstruments] = useState<InstrumentLibraryItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<InstrumentListFilters>({});

  const queryFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const result = await instrumentService.fetchInstruments(queryFilters);
      if (result.error) {
        toast.error(result.error);
        setInstruments([]);
      } else {
        setInstruments(result.data?.instruments || []);
      }
      setIsLoading(false);
    };
    load();
  }, [queryFilters]);

  const setFilter = <K extends keyof InstrumentListFilters>(
    key: K,
    value: InstrumentListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Instrument Library</h1>
          <p className="text-gray-600 mt-1">
            Reference catalog of funding instruments — terms, fit, complexity, and
            regulatory notes.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={filters.typical_stage || ''}
              onChange={(e) => setFilter('typical_stage', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All stages</option>
              {STAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              value={filters.complexity || ''}
              onChange={(e) =>
                setFilter('complexity', e.target.value as Complexity | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All complexity</option>
              {COMPLEXITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>

            <select
              value={
                filters.dilutive === undefined
                  ? ''
                  : filters.dilutive
                  ? 'true'
                  : 'false'
              }
              onChange={(e) => {
                const v = e.target.value;
                setFilters((prev) => ({
                  ...prev,
                  dilutive: v === '' ? undefined : v === 'true',
                }));
              }}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All instruments</option>
              <option value="true">Dilutive only</option>
              <option value="false">Non-dilutive only</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading instruments...
          </div>
        ) : instruments.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <Layers className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              No instruments found
            </h2>
            <p className="text-sm">
              The library may not be seeded yet. The backend will seed on first startup,
              or hit <code>POST /api/v1/instruments/seed</code>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {instruments.map((inst) => (
              <div
                key={inst.id}
                className="bg-white border border-gray-200 rounded-xl p-5 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {inst.display_name}
                    </h2>
                    <p className="text-xs text-gray-500">{inst.instrument_type}</p>
                  </div>
                  <BookOpen className="w-5 h-5 text-blue-500 flex-shrink-0" />
                </div>

                {inst.description && (
                  <p className="text-sm text-gray-700 line-clamp-3">{inst.description}</p>
                )}

                <div className="flex flex-wrap gap-2 text-xs">
                  {inst.complexity && (
                    <span
                      className={`px-2 py-0.5 font-medium rounded-full ${complexityBadge(
                        inst.complexity
                      )}`}
                    >
                      {formatLabel(inst.complexity)} complexity
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 font-medium rounded-full ${
                      inst.dilutive === false
                        ? 'bg-emerald-100 text-emerald-800'
                        : inst.dilutive
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {inst.dilutive === false
                      ? 'Non-dilutive'
                      : inst.dilutive
                      ? 'Dilutive'
                      : '—'}
                  </span>
                </div>

                <div className="text-xs text-gray-600 space-y-1">
                  <div>
                    <span className="text-gray-500">Typical amount:</span>{' '}
                    {formatMoney(inst.typical_amount_min)}–
                    {formatMoney(inst.typical_amount_max)}
                  </div>
                  {inst.typical_stage && inst.typical_stage.length > 0 && (
                    <div>
                      <span className="text-gray-500">Stages:</span>{' '}
                      {inst.typical_stage.map((s) => formatLabel(s)).join(', ')}
                    </div>
                  )}
                  {inst.investor_types && inst.investor_types.length > 0 && (
                    <div>
                      <span className="text-gray-500">Investor types:</span>{' '}
                      {inst.investor_types.map((s) => formatLabel(s)).join(', ')}
                    </div>
                  )}
                </div>

                {inst.best_for && (
                  <div className="text-xs text-gray-700 border-t border-gray-100 pt-2">
                    <span className="font-medium text-gray-900">Best for: </span>
                    {inst.best_for}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InstrumentsPage;
